import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Collection from 'assets/components/data/Collection';
import ThemeCollectionTable from 'assets/components/data/themes/Theme.Collection.Table';
import DataMap from 'assets/components/dataMap/DataMap';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { useState } from 'react';
import { AutomationRuleLike, AutomationRuleSchema } from '../helpers/AutomationTypes';
import { createRule } from '../helpers/ruleHelpers';
import AutomationRuleRow from './Automation.Rule.Row';

type Props<T extends AutomationRuleLike = AutomationRuleLike> = {
  value?: T[];
  defaultValue?: T[];
  schema: AutomationRuleSchema;
  readonly?: boolean;
  isReadOnlySummary?: boolean;
  hideAddRule?: boolean;
  hideDeleteRule?: boolean;
  createRuleData?: (rules: T[]) => Partial<T>;
  getRuleTitle?: (rule: T, index: number) => string;
  onChange?(rules: T[]): void;
  onDeleteRule?(index: number, rule: T): void;
};

export default function AutomationRulesTable<T extends AutomationRuleLike = AutomationRuleLike>(props: Props<T>) {
  const { lang } = getLocales();
  const [internalValue, setInternalValue] = useState<T[]>(props.defaultValue || []);
  const rules = props.value || internalValue;
  const isExpressionMode = Boolean(props.schema.allowMultipleExpressions);
  const isReadOnlySummary = props.isReadOnlySummary || props.readonly || props.schema.readonly;

  const commit = (nextRules: T[]) => {
    if (!props.value) setInternalValue(nextRules);
    props.onChange?.(nextRules);
  };

  const addRule = () => {
    const newRule = createRule(props.schema, props.createRuleData?.(rules) || {}) as T;
    commit([...(rules || []), newRule]);
  };

  const changeRule = (index: number, rule: AutomationRuleLike) => {
    commit((rules || []).map((item, itemIndex) => (itemIndex === index ? (rule as T) : item)));
  };

  const deleteRule = (index: number, rule: T) => {
    commit((rules || []).filter((_, itemIndex) => itemIndex !== index));
    props.onDeleteRule?.(index, rule);
  };

  return (
    <Collection class={ThemeCollectionTable}>
      {!isReadOnlySummary && !props.hideAddRule && (
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          {isExpressionMode && (
            <p
              style={{
                backgroundColor: Theme.Color.warning,
                color: 'white',
                padding: `${Theme.Size.M} ${Theme.Size.M}`,
                fontWeight: 'bold',
              }}
            >
              {lang.adjacentExpressionsImplicitAnd}
            </p>
          )}
          <Button
            class={ThemeButtonCircle}
            render={lang.addRule || lang.addNew}
            active
            disabled={!props.schema.allowMultipleRules && rules?.length >= 1}
            onClick={addRule}
          />
        </Box>
      )}
      <DataMap
        data={rules}
        render={({ data: rule, index }) => (
          <div key={rule.uid || rule.id || index}>
            {props.getRuleTitle && <h3>{props.getRuleTitle(rule, index)}</h3>}
            <AutomationRuleRow
              rule={rule}
              schema={props.schema}
              readonly={props.readonly}
              isReadOnlySummary={isReadOnlySummary}
              hideDeleteRule={props.hideDeleteRule}
              onChange={(updatedRule) => changeRule(index, updatedRule)}
              onDeleteRule={() => deleteRule(index, rule)}
            />
          </div>
        )}
      />
    </Collection>
  );
}
